import React, { useCallback, useContext, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import * as styles from '../../offices/modals/modal.styles';
import type Office from '../../offices/models/office';
import OfficeListCard from '../../offices/cards/office-list-card.component';
import Parking from '../models/parking';
import { IsMobileContext } from '../../../core/context/is-mobile';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import Grid from '../../../core/components/grid/grid.component';
import Row from '../../../core/components/row/row.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly allOffices: Office[];
  readonly handleCreate: (value: Parking) => void;
  readonly isParkingValid: (value: Parking) => boolean;
}

const CreateParkingModal = ({ showModal, handleClose, allOffices, handleCreate, isParkingValid }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [parking, setParking] = useState<Parking>(new Parking());
  const [selectedOffices, setSelectedOffices] = useState<Office[]>([]);
  const sortedOffices = allOffices.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));

  const resetModalState = (): void => {
    setSelectedOffices([]);
    setParking(new Parking());
  };

  const handleCloseModal = (): void => {
    resetModalState();
    handleClose();
  };

  const handleSave = (): void => {
    const parkingCopy = { ...parking };
    if (parkingCopy.capacity === 0) {
      parkingCopy.capacity = 1;
    }
    if (isParkingValid(parkingCopy)) {
      resetModalState();
      handleCreate(parkingCopy);
    }
  };

  const HandleParkingNameChange = useCallback(
    (value: string) => {
      const parkingCopy = { ...parking };
      parkingCopy.name = value;
      setParking(parkingCopy);
    },
    [setParking, parking],
  );

  const HandleParkingCapacityChange = useCallback(
    (value: string) => {
      const parkingCopy = { ...parking };
      if (Number.isFinite(Number(value)) && Number(value) >= 0) {
        parkingCopy.capacity = Number(value);
        setParking(parkingCopy);
      }
    },
    [setParking, parking],
  );

  const HandleParkingOfficeChange = useCallback(
    (office: Office) => {
      if (office.id === undefined) {
        return;
      }

      const parkingCopy = { ...parking };
      if (!parkingCopy.officeIds.some((officeId) => officeId === office.id)) {
        parkingCopy.officeIds.push(office.id);
        setParking(parkingCopy);
      } else {
        parkingCopy.officeIds = parkingCopy.officeIds.filter((officeId) => officeId !== office.id);
        setParking(parkingCopy);
      }
    },
    [setParking, parking],
  );

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCloseModal} style={styles.modalStyle(isMobile, 800)}>
      <Column className={style(styles.modalBody(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <span className={style(styles.columnHeader(isMobile, 8))}>{t('create-new-parking')}</span>
        <LabeledInput
          labelText={t('name').toString()}
          value={parking.name}
          onChange={(value): void => {
            HandleParkingNameChange(value);
          }}
          onEnter={(): void => {
            handleSave();
          }}
        />
        <LabeledInput
          labelText={t('number-of-parking-spots').toString()}
          type="number"
          value="1"
          onChange={(value): void => {
            HandleParkingCapacityChange(value);
          }}
        />
        <span className={style(styles.modalText)}>{t('your-offices')}</span>
        {sortedOffices.length === 0 ? (
          <span className={style(styles.emptyListMessage)}>{t('no-offices-available')}</span>
        ) : (
          <Grid
            rowClass={style(styles.gridRow)}
            itemSpacing={16}
            numberOfColumns={3}
            numberOfItems={sortedOffices.length}
            renderItem={(index): React.JSX.Element => {
              const officeItem = sortedOffices[index];
              return (
                officeItem && (
                  <div
                    className={isMobile ? style(styles.rowItem(true)) : undefined}
                    key={officeItem.id}
                    onClick={(): void => {
                      HandleParkingOfficeChange(officeItem);
                    }}
                    role="presentation"
                  >
                    <OfficeListCard office={officeItem} checked={selectedOffices.some((office) => office.id === officeItem.id)} />
                  </div>
                )
              );
            }}
          />
        )}
        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              onClick={(): void => {
                handleSave();
              }}
            >
              {t('create')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCloseModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default CreateParkingModal;
